import { useTranslation } from 'react-i18next';

import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { Callout } from '@/shared/components/feedback/Callout/Callout';
import { EmptyState } from '@/shared/components/feedback/EmptyState/EmptyState';
import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/Dialog/Dialog';

import { DecisionRefusal } from '../DecisionRefusal/DecisionRefusal';

import {
  useEntitlementRevokeModalLogic,
  type RevokeValues,
} from './useEntitlementRevokeModalLogic';

/**
 * Taking an entitlement back.
 *
 * Presentation only; the store's consequence, the ceremony and every refusal are
 * decided in `useEntitlementRevokeModalLogic` (U5).
 *
 * **Two slots, two different jobs.** The store warning goes in `banner`, above
 * the fields — "the store will keep charging them" has to be read before the
 * reason is written. The *refusal* goes in the form's `children`, under the
 * fields and above the actions, because that one explains the submit that has
 * just been refused and belongs where the operator's eye already is (P20b).
 */
export function EntitlementRevokeModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('payments');
  const view = useEntitlementRevokeModalLogic({
    entitlementId:
      typeof modalData?.['entitlementId'] === 'string' ? modalData['entitlementId'] : '',
    userId: typeof modalData?.['userId'] === 'string' ? modalData['userId'] : '',
    onDone: onCancel,
  });

  // The row left the member's list between the page rendering and the dialog
  // opening — a colleague revoked it, or a refund webhook did. There is nothing
  // to revoke, so the dialog says so rather than asking for a reason it could
  // never send.
  if (view.isMissing) {
    return (
      <Dialog
        open={open}
        onOpenChange={(isOpen) => {
          if (!isOpen) onCancel();
        }}
      >
        <DialogContent size="md">
          <DialogHeader>
            <DialogTitle>{view.title}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <EmptyState
              title={t('entitlements.error.notFound')}
              description={t('entitlements.error.notClaimable')}
            />
          </DialogBody>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <DynamicFormModal<RevokeValues>
      isOpen={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onCancel();
      }}
      title={view.title}
      description={view.description}
      size="md"
      isLoading={view.isLoading}
      fields={view.fields}
      schema={view.schema}
      initialValues={view.initialValues}
      submitLabel={view.submitLabel}
      onCancel={onCancel}
      onSubmit={view.submit}
      banner={
        <Callout
          tone={view.isStoreBilled ? 'danger' : 'warning'}
          liveness="alert"
          title={view.storeWarningTitle}
        >
          <div className="flex flex-col gap-sm">
            <p>{view.storeWarningBody}</p>
            {view.stillBillingWarning === null ? null : <p>{view.stillBillingWarning}</p>}
          </div>
        </Callout>
      }
    >
      <DecisionRefusal
        message={view.error}
        isConflict={view.isConflict}
        isGone={view.isGone}
        onReload={view.reload}
        onClose={onCancel}
      />
    </DynamicFormModal>
  );
}
